document.documentElement.classList.add('js');

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initNavigation() {
  document.querySelectorAll('[data-ln-navigation]').forEach((header) => {
    const button = header.querySelector('[data-ln-nav-toggle]');
    const drawer = header.querySelector('[data-ln-nav-drawer]');
    if (!button || !drawer) return;

    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const setOpen = (open, restoreFocus = true) => {
      button.setAttribute('aria-expanded', String(open));
      button.setAttribute('aria-label', open ? 'Menu sluiten' : 'Menu openen');
      drawer.classList.toggle('is-open', open);
      drawer.setAttribute('aria-hidden', String(!open));
      drawer.toggleAttribute('inert', !open);
      document.documentElement.classList.toggle('ln-nav-open', open);
      if (open) {
        const first = drawer.querySelector(focusableSelector);
        if (first) first.focus();
      } else if (restoreFocus) {
        button.focus();
      }
    };

    button.addEventListener('click', () => {
      setOpen(button.getAttribute('aria-expanded') !== 'true');
    });

    drawer.addEventListener('click', (event) => {
      if (event.target.closest('a')) setOpen(false);
    });

    header.addEventListener('keydown', (event) => {
      const open = button.getAttribute('aria-expanded') === 'true';
      if (!open) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(drawer.querySelectorAll(focusableSelector)).filter(
        (element) => !element.hasAttribute('hidden')
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const desktop = window.matchMedia('(min-width: 1181px)');
    const closeOnDesktop = (event) => {
      if (event.matches && button.getAttribute('aria-expanded') === 'true') setOpen(false, false);
    };
    desktop.addEventListener('change', closeOnDesktop);
  });
}

function initTabs() {
  document.querySelectorAll('[data-ln-tabs]').forEach((tabsRoot) => {
    const tabs = Array.from(tabsRoot.querySelectorAll('[role="tab"]'));
    const panels = Array.from(tabsRoot.querySelectorAll('[role="tabpanel"]'));
    if (!tabs.length || !panels.length) return;

    const activate = (tab, moveFocus = false) => {
      const targetId = tab.getAttribute('aria-controls');
      tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.id !== targetId;
      });
      if (moveFocus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        let targetIndex = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          targetIndex = (index + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          targetIndex = (index - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          targetIndex = 0;
        } else if (event.key === 'End') {
          targetIndex = tabs.length - 1;
        }

        if (targetIndex !== null) {
          event.preventDefault();
          activate(tabs[targetIndex], true);
        }
      });
    });

    activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
  });
}

function initReveals() {
  const elements = document.querySelectorAll('.ln-reveal');
  if (!elements.length) return;

  if (reducedMotion.matches || !('IntersectionObserver' in window)) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  elements.forEach((element) => observer.observe(element));
}

function initHeroPointer() {
  if (reducedMotion.matches || window.matchMedia('(pointer: coarse)').matches) return;

  document.querySelectorAll('[data-ln-pointer]').forEach((stage) => {
    stage.addEventListener('pointermove', (event) => {
      const rect = stage.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      stage.style.setProperty('--pointer-x', x.toFixed(2) + '%');
      stage.style.setProperty('--pointer-y', y.toFixed(2) + '%');
    });
  });
}

function initProjectForms() {
  document.querySelectorAll('.ln-project-form').forEach((form) => {
    const steps = Array.from(form.querySelectorAll('[data-ln-form-step]'));
    const progress = Array.from(form.querySelectorAll('[data-ln-progress-step]'));
    if (steps.length < 2) return;

    let current = 0;

    const showStep = (index, focus = true) => {
      current = Math.max(0, Math.min(index, steps.length - 1));
      steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== current;
      });
      progress.forEach((item, stepIndex) => {
        item.classList.toggle('is-active', stepIndex <= current);
      });
      form.dataset.currentStep = String(current + 1);
      if (focus) {
        const legend = steps[current].querySelector('legend');
        if (legend) {
          legend.tabIndex = -1;
          legend.focus();
        }
      }
    };

    const validateCurrentStep = () => {
      const fields = Array.from(
        steps[current].querySelectorAll('input, select, textarea')
      ).filter((field) => !field.disabled);

      for (const field of fields) {
        if (!field.checkValidity()) {
          field.reportValidity();
          field.focus();
          return false;
        }
      }
      return true;
    };

    form.addEventListener('click', (event) => {
      const next = event.target.closest('[data-ln-next]');
      const previous = event.target.closest('[data-ln-previous]');
      if (next) {
        event.preventDefault();
        if (validateCurrentStep()) showStep(current + 1);
      }
      if (previous) {
        event.preventDefault();
        showStep(current - 1);
      }
    });

    form.addEventListener('submit', (event) => {
      for (let index = 0; index < steps.length; index += 1) {
        current = index;
        if (!validateCurrentStep()) {
          event.preventDefault();
          showStep(index, false);
          return;
        }
      }
    });

    showStep(0, false);
  });
}

function initAnchorNavigation() {
  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const id = link.getAttribute('href');
      if (!id || id === '#') return;
      const target = document.querySelector(id);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
      target.focus({ preventScroll: true });
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initNavigation();
  initTabs();
  initReveals();
  initHeroPointer();
  initProjectForms();
  initAnchorNavigation();
});
